import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  ChevronLeft, ShoppingCart, Trash2, Plus, Minus, StickyNote, Send, Package, AlertTriangle, Sparkles, History,
} from "lucide-react";
import {
  loadCart, saveCart, clearCart, cartLineCount, cartSubtotal,
} from "@/utils/clientCart";
import { useClientDestination } from "@/utils/clientDestination";
import { DestinationSelector } from "@/components/DestinationSelector";
import { submitOrderWithOfflineFallback } from "@/utils/pendingOrderSync";

const fmtEur = (n) =>
  (Number(n) || 0).toLocaleString("it-IT", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

function CartRow({ item, onChange, onRemove, hidePrice = false }) {
  const [showNote, setShowNote] = useState(!!item.note);
  const qty = Number(item.quantity) || 0;
  const unitNet = useMemo(() => {
    let n = Number(item.price_unit) || 0;
    for (const s of [item.sc1, item.sc2, item.sc3, item.sc4]) {
      const v = Number(s) || 0;
      if (v > 0) n *= 1 - v / 100;
    }
    return Math.round(n * 1000) / 1000;
  }, [item.price_unit, item.sc1, item.sc2, item.sc3, item.sc4]);
  const lineTotal = useMemo(() => Math.round(qty * unitNet * 100) / 100, [qty, unitNet]);
  const hasDiscount = (Number(item.price_list) || 0) > unitNet && (Number(item.price_list) || 0) > 0;
  const isPromo = item.price_source === "promo";
  const isLastPrice = item.price_source === "last_paid";

  const setQty = (v) => onChange({ ...item, quantity: Math.max(0, Number(v) || 0) });
  const setNote = (v) => onChange({ ...item, note: v });

  return (
    <div
      data-testid={`cart-row-${item.product_id}`}
      className="bg-[#18181b] border border-white/5 rounded-lg p-3 space-y-2"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-white leading-tight line-clamp-2">
            {item.product_name}
          </p>
          <p className="text-[10px] text-zinc-500 font-mono mt-0.5">
            {item.product_sku} · {item.unit || "pz"}
          </p>
        </div>
        <button
          onClick={onRemove}
          data-testid={`cart-remove-${item.product_id}`}
          className="p-1.5 rounded text-zinc-500 hover:text-red-400 hover:bg-red-500/10"
          title="Rimuovi"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="text-[11px] text-zinc-400">
          {hasDiscount && (
            <>
              Listino:&nbsp;
              <span className="line-through text-zinc-600 mr-1.5">
                € {fmtEur(item.price_list)}
              </span>
            </>
          )}
          <span className={`font-mono font-semibold ${isPromo ? "text-red-400" : isLastPrice ? "text-amber-400" : "text-cyan-400"}`}>
            € {fmtEur(unitNet)}
          </span>
        </div>
        {isPromo && (
          <span className="text-[9px] px-1.5 py-0.5 rounded bg-red-500/15 text-red-300 font-bold uppercase tracking-wider">
            Promo
          </span>
        )}
        {isLastPrice && (
          <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 font-bold uppercase tracking-wider">
            Ultimo prezzo
          </span>
        )}
      </div>

      <div>
        <label className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1 block">
          Quantità
        </label>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setQty(qty - 1)}
            data-testid={`cart-qty-minus-${item.product_id}`}
            className="w-9 h-9 rounded-md bg-white/5 hover:bg-white/10 text-zinc-300 flex items-center justify-center"
          >
            <Minus className="w-4 h-4" />
          </button>
          <Input
            type="number"
            inputMode="decimal"
            min="0"
            value={qty}
            onChange={(e) => setQty(e.target.value)}
            className="no-spin h-9 px-2 text-center bg-[#0f0f11] border-white/10 text-white font-mono"
            data-testid={`cart-qty-${item.product_id}`}
          />
          <button
            type="button"
            onClick={() => setQty(qty + 1)}
            data-testid={`cart-qty-plus-${item.product_id}`}
            className="w-9 h-9 rounded-md bg-white/5 hover:bg-white/10 text-zinc-300 flex items-center justify-center"
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="flex items-center justify-between pt-1 border-t border-white/5">
        <button
          type="button"
          onClick={() => setShowNote((v) => !v)}
          data-testid={`cart-note-toggle-${item.product_id}`}
          className={`inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded ${
            item.note
              ? "bg-amber-500/10 text-amber-300"
              : "bg-white/5 text-zinc-400 hover:text-white"
          }`}
        >
          <StickyNote className="w-3.5 h-3.5" />
          {item.note ? "Nota" : "+ Nota"}
        </button>
        <div className="text-right">
          <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Totale riga</p>
          <p className="text-base text-white font-mono font-bold">€ {fmtEur(lineTotal)}</p>
        </div>
      </div>

      {showNote && (
        <Textarea
          value={item.note || ""}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Es. confezionare separatamente, scadenza lunga, richiesta reso, ecc."
          rows={2}
          className="bg-[#0f0f11] border-white/10 text-white text-sm resize-none"
          data-testid={`cart-note-${item.product_id}`}
        />
      )}
    </div>
  );
}

function FrequentItemsPanel({ items, cart, onAdd, hidePrice = false }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="mt-5" data-testid="cart-frequent-items">
      <div className="flex items-center gap-2 mb-2">
        <History className="w-4 h-4 text-cyan-400" />
        <h3 className="text-sm font-semibold text-white">
          I miei articoli frequenti
        </h3>
        <span className="text-[10px] text-zinc-500">({items.length})</span>
      </div>
      <div className="space-y-1.5">
        {items.map((p) => {
          const inCart = cart[p.id]?.quantity || 0;
          const promoActive = Number(p.promo_price) > 0;
          const showPrice = promoActive ? Number(p.promo_price) : Number(p.ultimo_prezzo);
          const d = p.ultima_data || "";
          const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(d);
          const fmtDate = m ? `${m[3]}/${m[2]}/${m[1].slice(2)}` : d;
          return (
            <div
              key={p.id}
              data-testid={`freq-row-${p.id}`}
              className="flex items-center gap-2 bg-[#18181b] border border-white/5 rounded-lg p-2 hover:border-cyan-500/30 transition"
            >
              <div className="w-12 h-12 shrink-0 rounded bg-[#0f0f11] flex items-center justify-center overflow-hidden">
                {p.image_url ? (
                  <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
                ) : (
                  <Package className="w-5 h-5 text-zinc-700" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[12px] font-semibold text-white leading-tight line-clamp-2">
                  {p.name}
                </p>
                <div className="flex items-center gap-1.5 mt-0.5 text-[10px] text-zinc-500 flex-wrap">
                  <span className="font-mono">{p.sku}</span>
                  <span>·</span>
                  <span className="font-mono">
                    {Number(p.ultima_qta).toLocaleString("it-IT", { maximumFractionDigits: 2 })} {p.unit || "pz"}
                  </span>
                  <span>·</span>
                  <span className="font-mono">{fmtDate}</span>
                </div>
                {!hidePrice && (
                <div className="flex items-center gap-1.5 mt-0.5">
                  {promoActive ? (
                    <span className="text-[9px] px-1 py-0.5 rounded bg-red-500/15 text-red-300 font-bold uppercase">
                      Promo
                    </span>
                  ) : (
                    <span className="text-[9px] px-1 py-0.5 rounded bg-amber-500/10 text-amber-300 font-bold uppercase">
                      Ultimo
                    </span>
                  )}
                  <span className={`text-sm font-bold font-mono ${promoActive ? "text-red-400" : "text-amber-400"}`}>
                    € {fmtEur(showPrice)}
                  </span>
                </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => onAdd(p)}
                data-testid={`freq-add-${p.id}`}
                className={`shrink-0 inline-flex items-center justify-center gap-1 h-9 px-3 rounded-md font-bold text-xs transition active:scale-95 ${
                  inCart > 0
                    ? "bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30"
                    : "bg-amber-500 hover:bg-amber-600 text-black"
                }`}
              >
                <Plus className="w-3.5 h-3.5" />
                {inCart > 0 ? `+1 (${inCart})` : "+1"}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function ClientCart() {
  const navigate = useNavigate();
  const { user, authAxios } = useAuth();
  const clientId = user?.client_id || null;
  // Cliente contrattualizzato (sconti_fissi): TUTTI i prezzi nascosti
  const hidePrices = !!user?.sconti_fissi;
  const [cart, setCart] = useState({});
  const [generalNote, setGeneralNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [recommendations, setRecommendations] = useState([]);
  const [frequentItems, setFrequentItems] = useState([]);
  // Apertura forzata del selettore punto vendita (es. submit senza scelta)
  const [forceDestOpen, setForceDestOpen] = useState(false);

  const {
    destinations: clientDestinations,
    selectedDest,
    setSelectedDest,
    isLocked: isDestLocked,
    isHidden: isDestHidden,
    needsChoice: destNeedsChoice,
  } = useClientDestination(user, authAxios);

  useEffect(() => {
    setCart(loadCart(clientId));
  }, [clientId]);

  // Self-heal: ripara righe carrello con metadati mancanti (es. aggiunte da vecchie versioni).
  useEffect(() => {
    if (!clientId) return;
    const raw = loadCart(clientId);
    const ids = Object.keys(raw || {});
    if (ids.length === 0) return;
    const broken = ids.filter(
      (pid) => !raw[pid]?.product_name || raw[pid]?.product_name === ""
    );
    if (broken.length === 0) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await authAxios.get("/products");
        if (cancelled) return;
        const byId = Object.fromEntries((r.data || []).map((p) => [p.id, p]));
        const patched = { ...raw };
        let changed = false;
        for (const pid of broken) {
          const p = byId[pid];
          if (!p) continue;
          const listino = Number(p.price_list) || 0;
          const existing = patched[pid];
          patched[pid] = {
            ...existing,
            product_id: pid,
            product_name: existing.product_name || p.name || "",
            product_sku: existing.product_sku || p.sku || "",
            unit: existing.unit || p.unit || "pz",
            iva_percentage: Number(existing.iva_percentage) || Number(p.iva_percentage) || 10,
            price_unit: Number(existing.price_unit) > 0 ? existing.price_unit : listino,
            price_list: Number(existing.price_list) > 0 ? existing.price_list : listino,
            price_source: existing.price_source || "listino",
            sc1: Number(existing.sc1) || 0,
            sc2: Number(existing.sc2) || 0,
            sc3: Number(existing.sc3) || 0,
            sc4: Number(existing.sc4) || 0,
            note: existing.note || "",
          };
          changed = true;
        }
        if (changed) {
          saveCart(clientId, patched);
          setCart(patched);
        }
      } catch {
        /* silent — cart will still work, just without enriched names */
      }
    })();
    return () => { cancelled = true; };
  }, [clientId, authAxios]);

  // Frequent items: products the client has bought before (filtered by selected destination).
  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await authAxios.get("/client/frequent-items", {
          params: { limit: 30, ...(selectedDest ? { dest_id: selectedDest } : {}) },
        });
        if (!cancelled) setFrequentItems(r.data?.items || []);
      } catch {
        if (!cancelled) setFrequentItems([]);
      }
    })();
    return () => { cancelled = true; };
  }, [authAxios, clientId, selectedDest]);

  // Fetch personalized recommendations (active promos + best sellers).
  // Refreshed when the cart line-set changes so suggestions don't repeat what's already added.
  const cartIds = useMemo(() => Object.keys(cart || {}).sort().join(","), [cart]);
  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await authAxios.get("/client/recommended-products", {
          params: { exclude: cartIds, limit: 6 },
        });
        if (!cancelled) setRecommendations(r.data?.items || []);
      } catch {
        if (!cancelled) setRecommendations([]);
      }
    })();
    return () => { cancelled = true; };
  }, [authAxios, clientId, cartIds]);

  const items = useMemo(
    () => Object.values(cart).filter((it) => (Number(it.quantity) || 0) > 0),
    [cart]
  );
  const grossSubtotal = useMemo(() => cartSubtotal(cart), [cart]);
  const scontoCanalePct = Number(user?.sconto_canale_pct) || 0;
  const scontoCanaleAmount = useMemo(
    () => Math.round(grossSubtotal * (scontoCanalePct / 100) * 100) / 100,
    [grossSubtotal, scontoCanalePct]
  );
  const subtotal = useMemo(
    () => Math.round((grossSubtotal - scontoCanaleAmount) * 100) / 100,
    [grossSubtotal, scontoCanaleAmount]
  );
  const lineCount = items.length;

  const updateItem = (next) => {
    setCart((prev) => {
      const upd = { ...prev, [next.product_id]: next };
      // Auto-prune zero-quantity rows
      if ((Number(next.quantity) || 0) === 0) {
        delete upd[next.product_id];
      }
      saveCart(clientId, upd);
      return upd;
    });
  };

  const removeItem = (productId) => {
    setCart((prev) => {
      const upd = { ...prev };
      delete upd[productId];
      saveCart(clientId, upd);
      return upd;
    });
  };

  const handleClear = () => {
    if (!window.confirm("Svuotare il carrello?")) return;
    clearCart(clientId);
    setCart({});
  };

  const addFrequentItem = (product) => {
    if (!clientId) return;
    setCart((prev) => {
      const existing = prev[product.id];
      // Resolve price: PROMO > LAST_PAID > LISTINO
      let priceUnit = Number(product.price_list) || 0;
      let priceSource = "listino";
      if (Number(product.promo_price) > 0) {
        priceUnit = Number(product.promo_price);
        priceSource = "promo";
      } else if (Number(product.ultimo_prezzo) > 0) {
        priceUnit = Number(product.ultimo_prezzo);
        priceSource = "last_paid";
      }
      const upd = {
        ...prev,
        [product.id]: {
          product_id: product.id,
          product_name: product.name,
          product_sku: product.sku || "",
          unit: product.unit || "pz",
          iva_percentage: Number(product.iva_percentage) || 10,
          price_unit: priceUnit,
          price_list: Number(product.price_list) || 0,
          price_source: priceSource,
          quantity: (Number(existing?.quantity) || 0) + 1,
          note: existing?.note || "",
          sc1: 0, sc2: 0, sc3: 0, sc4: 0,
        },
      };
      saveCart(clientId, upd);
      return upd;
    });
    toast.success(`${product.name.slice(0, 35)} aggiunto`);
  };

  const addRecommended = (product) => {
    if (!clientId) return;
    setCart((prev) => {
      const existing = prev[product.id];
      const upd = {
        ...prev,
        [product.id]: {
          product_id: product.id,
          product_name: product.name,
          product_sku: product.sku || "",
          unit: product.unit || "pz",
          iva_percentage: Number(product.iva_percentage) || 10,
          price_unit: Number(product.price_list) || 0,
          price_list: Number(product.price_list) || 0,
          quantity: (Number(existing?.quantity) || 0) + 1,
          qty_omaggio: existing?.qty_omaggio || 0,
          note: existing?.note || "",
          sc1: 0, sc2: 0, sc3: 0, sc4: 0,
        },
      };
      saveCart(clientId, upd);
      return upd;
    });
    toast.success(`${product.name.slice(0, 35)} aggiunto`);
  };

  const handleSubmit = async () => {
    if (!clientId) {
      toast.error("Profilo non collegato a un'anagrafica");
      return;
    }
    if (lineCount === 0) {
      toast.error("Carrello vuoto");
      return;
    }
    // Block checkout if the client has multiple destinations and none was selected:
    // apri direttamente il selettore del punto vendita
    if (!isDestHidden && !selectedDest) {
      setForceDestOpen(true);
      toast.info("Scegli il punto vendita per cui stai facendo l'ordine");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        // server forces client_id from user.client_id, but we keep these for API contract
        client_name: user?.name || "Cliente",
        dest_id: selectedDest || null,
        items: items.map((it) => ({
          product_id: it.product_id,
          product_name: it.product_name,
          product_sku: it.product_sku || "",
          quantity: Number(it.quantity) || 0,
          qty_omaggio: 0,
          unit: it.unit || "pz",
          price_unit: Number(it.price_unit) || 0,
          sc1: Number(it.sc1) || 0,
          sc2: Number(it.sc2) || 0,
          sc3: Number(it.sc3) || 0,
          sc4: Number(it.sc4) || 0,
          iva_percentage: Number(it.iva_percentage) || 10,
          note: it.note || null,
        })),
        note: generalNote.trim() || null,
      };
      const res = await submitOrderWithOfflineFallback(authAxios, payload, { source: "client_cart" });
      clearCart(clientId);
      setCart({});
      if (res.online) {
        toast.success(`Ordine ${res.order?.numero || ""} inviato!`, { duration: 4000 });
      } else {
        toast.success("Ordine salvato offline — verrà inviato automaticamente quando torni online", { duration: 5000 });
      }
      navigate("/client/orders");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore invio ordine");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090b] pb-32">
      <header className="sticky top-0 z-40 bg-[#09090b]/95 backdrop-blur border-b border-white/5">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => navigate("/catalog")}
            data-testid="cart-back-btn"
            className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-zinc-300"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="font-heading text-lg font-bold text-white flex items-center gap-2">
              <ShoppingCart className="w-5 h-5 text-amber-400" />
              Il tuo carrello
            </h1>
            <p className="text-[11px] text-zinc-500">
              {lineCount} {lineCount === 1 ? "articolo" : "articoli"}
            </p>
          </div>
          {lineCount > 0 && (
            <button
              onClick={handleClear}
              data-testid="cart-clear-btn"
              className="text-[11px] text-zinc-400 hover:text-red-400 px-2 py-1 rounded"
            >
              Svuota
            </button>
          )}
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-4">
        {clientId && !isDestHidden && (
          <div className="mb-4">
            <DestinationSelector
              destinations={clientDestinations}
              selectedDest={selectedDest}
              onChange={setSelectedDest}
              isLocked={isDestLocked}
              forceOpen={destNeedsChoice || forceDestOpen}
              onForceClose={() => setForceDestOpen(false)}
              mandatory
            />
          </div>
        )}
        {!clientId ? (
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4 flex gap-3 items-start">
            <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-amber-200 text-sm font-semibold">Profilo non collegato</p>
              <p className="text-amber-200/70 text-xs mt-1">
                Per poter inviare ordini il tuo account deve essere associato ad un'anagrafica
                cliente. Contatta l'amministratore per ricevere l'abilitazione.
              </p>
            </div>
          </div>
        ) : lineCount === 0 ? (
          <>
            <div className="text-center py-10">
              <Package className="w-14 h-14 text-zinc-700 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-zinc-400 mb-1">Il carrello è vuoto</h3>
              <p className="text-zinc-500 text-sm mb-4">
                Aggiungi prodotti dal catalogo o riordina rapidamente dai tuoi articoli frequenti qui sotto.
              </p>
              <Link to="/catalog">
                <Button
                  size="sm"
                  className="bg-amber-500 hover:bg-amber-600 text-black font-medium"
                  data-testid="cart-go-catalog"
                >
                  Vai al catalogo
                </Button>
              </Link>
            </div>
            <FrequentItemsPanel
              items={frequentItems}
              cart={cart}
              onAdd={addFrequentItem}
              hidePrice={hidePrices}
            />
          </>
        ) : (
          <>
            <div className="space-y-2">
              {items.map((it) => (
                <CartRow
                  key={it.product_id}
                  item={it}
                  onChange={updateItem}
                  onRemove={() => removeItem(it.product_id)}
                  hidePrice={hidePrices}
                />
              ))}
            </div>

            <FrequentItemsPanel
              items={frequentItems}
              cart={cart}
              onAdd={addFrequentItem}
              hidePrice={hidePrices}
            />

            {recommendations.length > 0 && (
              <div className="mt-5" data-testid="cart-recommendations">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-4 h-4 text-amber-400" />
                  <h3 className="text-sm font-semibold text-white">
                    Ti potrebbero interessare
                  </h3>
                </div>
                <div className="flex gap-2 overflow-x-auto -mx-1 px-1 pb-2 scrollbar-hide snap-x">
                  {recommendations.map((p) => (
                    <button
                      type="button"
                      key={p.id}
                      onClick={() => addRecommended(p)}
                      data-testid={`reco-add-${p.id}`}
                      className="snap-start shrink-0 w-40 text-left bg-[#18181b] border border-white/5 rounded-lg p-2 hover:border-amber-500/40 active:scale-[0.98] transition"
                    >
                      <div className="aspect-square w-full rounded-md bg-[#0f0f11] mb-2 flex items-center justify-center overflow-hidden">
                        {p.image_url ? (
                          <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
                        ) : (
                          <Package className="w-7 h-7 text-zinc-700" />
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 mb-1">
                        {p.reason === "promo" ? (
                          <span className="text-[9px] px-1.5 py-0.5 rounded bg-red-500/15 text-red-300 font-bold uppercase tracking-wider">
                            Promo
                          </span>
                        ) : (
                          <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 font-bold uppercase tracking-wider">
                            Top
                          </span>
                        )}
                      </div>
                      <p className="text-[11px] font-semibold text-white leading-tight line-clamp-2 min-h-[28px]">
                        {p.name}
                      </p>
                      <div className="mt-1.5 flex items-center justify-between">
                        {!hidePrices ? (
                          <span className="text-[11px] text-zinc-400 font-mono">
                            € {fmtEur(p.price_list)}
                          </span>
                        ) : <span />}
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded bg-amber-500 text-black">
                          <Plus className="w-3.5 h-3.5" />
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-4">
              <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">
                Note generali ordine
              </label>
              <Textarea
                value={generalNote}
                onChange={(e) => setGeneralNote(e.target.value)}
                placeholder="Indicazioni di consegna, orari, riferimenti..."
                rows={3}
                className="bg-[#18181b] border-white/10 text-white text-sm"
                data-testid="cart-general-note"
              />
            </div>

            <div className="mt-4 p-3 rounded-lg bg-amber-500/5 border border-amber-500/20 text-[11px] text-amber-200/80 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
              <p>
                <span className="font-semibold text-amber-200">Eventuali richieste di reso vanno indicate nelle note.</span>{" "}
                {!hidePrices && (
                  <>
                    I prezzi mostrati includono i tuoi sconti in vigore
                    {scontoCanalePct > 0 && (
                      <> e lo <span className="text-emerald-300 font-semibold">sconto canale del {scontoCanalePct.toFixed(0)}%</span> applicato sul subtotale</>
                    )}
                    . L'IVA viene calcolata al momento dell'invio.{" "}
                  </>
                )}
                L'ordine arriva in stato{" "}
                <span className="text-amber-400 font-semibold">Da approvare</span> e l'amministratore
                lo confermerà.
              </p>
            </div>
          </>
        )}
      </main>

      {/* Sticky bottom bar */}
      {clientId && lineCount > 0 && (
        <div className="fixed bottom-0 inset-x-0 z-50 bg-[#0f0f11]/95 backdrop-blur border-t border-white/10">
          <div className="max-w-3xl mx-auto px-4 py-3">
            {!hidePrices && scontoCanalePct > 0 && (
              <div className="space-y-0.5 mb-2 text-[11px] font-mono">
                <div className="flex items-center justify-between text-zinc-400">
                  <span>Subtotale lordo</span>
                  <span>€ {fmtEur(grossSubtotal)}</span>
                </div>
                <div className="flex items-center justify-between text-emerald-400">
                  <span>Sconto canale −{scontoCanalePct.toFixed(0)}%</span>
                  <span>− € {fmtEur(scontoCanaleAmount)}</span>
                </div>
              </div>
            )}
            <div className="flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-[10px] text-zinc-500 uppercase tracking-wider">
                  {hidePrices ? "Articoli" : scontoCanalePct > 0 ? "Subtotale netto" : "Subtotale"}
                </p>
                <p className="text-xl font-bold text-white font-mono" data-testid="cart-bottom-summary">
                  {hidePrices ? lineCount : `€ ${fmtEur(subtotal)}`}
                </p>
              </div>
              <Button
                onClick={handleSubmit}
                disabled={submitting}
                data-testid="cart-submit-btn"
                size="lg"
                className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold"
              >
                <Send className="w-4 h-4 mr-2" />
                {submitting ? "Invio..." : "Invia Ordine"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
